import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { DataTable } from '@ooh/ui/components/data-table';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { usePersistedDataTableColumns } from '../../../hooks/use-persisted-data-table-columns';
import { Badge } from '@ooh/ui/components/badge';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { Label } from '@ooh/ui/components/label';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { TableSkeletonRows } from '@ooh/ui/components/table-skeleton-rows';
import { useMeasuredElement } from '@ooh/ui/components/use-measured-element';
import type { Poi, PoiCategory } from '@ooh/contracts';
import { poiQueries } from '../api/queries';
import type { WithCallWithAuth } from '../api/queries';
import { useTogglePoiInternal } from '../api/mutations';
import { getCategoryLabel } from '../utils/category-label';
import { encodeCategoryKey, decodeCategoryKey } from '../utils/poi-constants';
import { formatDisplayDate } from '../../../lib/date-format';

interface PoiListProps {
  auth: WithCallWithAuth;
  onDelete: (poi: Poi) => void;
  onCreate: () => void;
}

type TogglePoiPayload = { id: string; internal_only: boolean };
type ToggleMutation = Pick<
  UseMutationResult<unknown, Error, TogglePoiPayload>,
  'mutate' | 'isPending' | 'variables'
>;

type NavigateFn = ReturnType<typeof useNavigate>;

function buildColumns(
  t: ReturnType<typeof useTranslation<'map'>>['t'],
  categories: PoiCategory[],
  onDelete: (poi: Poi) => void,
  toggleMutation: ToggleMutation,
  navigate: NavigateFn,
): ColumnDef<Poi>[] {
  return [
    {
      id: 'name',
      label: t('admin.pois.columns.name'),
      align: 'left' as const,
      renderCell: (row) => (
        <span className={row.deletedAt ? 'line-through text-muted-foreground' : 'font-medium'}>
          {row.name}
        </span>
      ),
    },
    {
      id: 'category',
      label: t('admin.pois.columns.category'),
      align: 'left' as const,
      renderCell: (row) => {
        const cat = categories.find(
          (c) => c.osm_key === row.osmKey && c.osm_value === row.osmValue,
        );
        return (
          <span className={row.deletedAt ? 'text-muted-foreground' : ''}>
            {cat ? getCategoryLabel(cat, t) : `${row.osmKey}/${row.osmValue}`}
          </span>
        );
      },
    },
    {
      id: 'internalOnly',
      label: t('admin.pois.columns.internalOnly'),
      align: 'left' as const,
      renderCell: (row) =>
        row.internalOnly ? (
          <Badge variant="outline" data-testid={`internal-badge-${row.id}`}>
            {t('admin.pois.internal')}
          </Badge>
        ) : null,
    },
    {
      id: 'status',
      label: '',
      align: 'left' as const,
      renderCell: (row) =>
        row.deletedAt ? <Badge variant="destructive">{t('admin.pois.deletedBadge')}</Badge> : null,
    },
    {
      id: 'createdAt',
      label: t('admin.pois.columns.createdAt'),
      align: 'left' as const,
      renderCell: (row) => (
        <span className={row.deletedAt ? 'text-muted-foreground' : ''}>
          {formatDisplayDate(row.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      renderCell: (row) => {
        if (row.deletedAt) return null;

        const isThisRowPending =
          toggleMutation.isPending && toggleMutation.variables?.id === row.id;
        const isCustom = row.source === 'custom';

        const toggleLabel = row.internalOnly
          ? t('admin.pois.makePublicButton')
          : t('admin.pois.makeInternalButton');

        const viewOnMapLabel = t('admin.pois.viewOnMapButton');

        return (
          <div className="flex items-center justify-end gap-1">
            {isCustom ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label={toggleLabel}
                  data-testid={`toggle-internal-${row.id}`}
                  disabled={isThisRowPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMutation.mutate({ id: row.id, internal_only: !row.internalOnly });
                  }}
                >
                  {row.internalOnly ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label={viewOnMapLabel}
                  data-testid={`view-on-map-${row.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigate({
                      to: '/map',
                      search: { lat: row.location.lat, lng: row.location.lng, zoom: 15 },
                    });
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="xs"
                  data-testid={`delete-poi-${row.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(row);
                  }}
                >
                  {t('admin.pois.deleteButton')}
                </Button>
              </>
            ) : null}
          </div>
        );
      },
    },
  ];
}

export function PoiList({ auth, onDelete, onCreate }: PoiListProps) {
  const { t } = useTranslation('map');
  const navigate = useNavigate({ from: '/admin/pois/' });
  const toggleMutation = useTogglePoiInternal(auth);
  const search = useSearch({ from: '/admin/pois/' });
  const osmKeyFilter = search.osmKey ?? '';
  const osmValueFilter = search.osmValue ?? '';
  const showDeleted = search.showDeleted ?? false;

  const { data: poisData, isLoading } = useQuery(
    poiQueries.list(auth, {
      source: 'custom',
      includeDeleted: showDeleted,
      osmKey: osmKeyFilter || undefined,
      osmValue: osmValueFilter || undefined,
    }),
  );
  const { data: categoriesData } = useQuery(poiQueries.categories(auth));

  const pois = Array.isArray(poisData) ? poisData : [];

  const categoryOptions = React.useMemo(() => {
    const cats = Array.isArray(categoriesData) ? categoriesData : [];
    return [
      { value: 'all', label: t('admin.pois.allCategories') },
      ...cats.map((cat) => ({
        value: encodeCategoryKey(cat.osm_key, cat.osm_value),
        label: getCategoryLabel(cat, t),
      })),
    ];
  }, [categoriesData, t]);

  const columns = React.useMemo(
    () =>
      buildColumns(
        t,
        Array.isArray(categoriesData) ? categoriesData : [],
        onDelete,
        toggleMutation,
        navigate,
      ),
    [t, categoriesData, onDelete, toggleMutation, navigate],
  );
  const persistedTable = usePersistedDataTableColumns(columns, 'ooh:admin-pois');
  const [toolbarHeight, toolbarRef] = useMeasuredElement<HTMLDivElement>(
    (node) => node.getBoundingClientRect().height,
  );

  return (
    <div data-testid="poi-list">
      <div
        ref={toolbarRef}
        className="sticky top-0 z-10 flex items-center justify-between mb-4 gap-2 flex-wrap bg-background"
      >
        <h2 className="text-[18px] font-semibold">{t('admin.pois.heading')}</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-52">
            <SearchableSelectField
              options={categoryOptions}
              value={osmKeyFilter ? encodeCategoryKey(osmKeyFilter, osmValueFilter) : 'all'}
              onChange={(v) => {
                void navigate({
                  search: (prev) => {
                    const next = { ...prev };
                    if (!v || v === 'all') {
                      delete next['osmKey'];
                      delete next['osmValue'];
                    } else {
                      const [key, value] = decodeCategoryKey(v);
                      next['osmKey'] = key;
                      next['osmValue'] = value;
                    }
                    return next;
                  },
                  replace: true,
                });
              }}
              data-testid="poi-category-filter"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-deleted-pois"
              checked={showDeleted}
              onCheckedChange={(checked) => {
                void navigate({
                  search: (prev) => {
                    const next = { ...prev };
                    if (checked === true) {
                      next['showDeleted'] = true;
                    } else {
                      delete next['showDeleted'];
                    }
                    return next;
                  },
                  replace: true,
                });
              }}
              data-testid="show-deleted-toggle"
            />
            <Label htmlFor="show-deleted-pois" className="text-[13px] cursor-pointer">
              {t('admin.pois.showDeletedLabel')}
            </Label>
          </div>
          <Button type="button" size="sm" onClick={onCreate} data-testid="add-poi-button">
            {t('admin.pois.addButton')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeletonRows rows={3} />
      ) : (
        <DataTable
          columns={persistedTable.columns}
          data={pois}
          getRowKey={(row) => row.id}
          onColumnResizeEnd={persistedTable.onColumnResizeEnd}
          getRowClassName={(row) => (row.deletedAt ? 'opacity-60' : undefined)} // eslint-disable-line i18next/no-literal-string
          stickyHeaderOffsetPx={toolbarHeight}
        />
      )}
    </div>
  );
}
